// Elevator floor picker — a tiny centred panel listing the floors the lobby elevator serves.
// Opened by world3d when the player presses E at the elevator doors; picking a floor teleports
// them (client zone travel, like a door portal) and closes the panel.

import { injectTheme } from "./theme.js";

const STYLE_ID = "ghostland-elevator-style";
const OVERLAY_ID = "gl-elevator";
const CSS = `
#${OVERLAY_ID} { position: fixed; inset: 0; z-index: 36; display: none; align-items: center; justify-content: center;
  background: rgba(3,1,6,.55); font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; }
#${OVERLAY_ID}.open { display: flex; }
#${OVERLAY_ID} .gl-ev-card { width: 260px; background: var(--gl-panel, #14101c); border: 1px solid var(--gl-border, #3a3450);
  border-radius: 10px; box-shadow: 0 18px 60px rgba(0,0,0,.6); color: var(--gl-text, #f2f0f7); overflow: hidden; }
#${OVERLAY_ID} .gl-ev-head { display: flex; align-items: center; justify-content: space-between; padding: 12px 14px;
  border-bottom: 1px solid var(--gl-border, #3a3450); }
#${OVERLAY_ID} .gl-ev-title { font-size: 13px; letter-spacing: .08em; }
#${OVERLAY_ID} .gl-ev-x { cursor: pointer; color: var(--gl-muted, #9a93b0); font-size: 16px; line-height: 1; padding: 2px 6px; }
#${OVERLAY_ID} .gl-ev-x:hover { color: var(--gl-text, #fff); }
#${OVERLAY_ID} .gl-ev-body { padding: 12px 14px 14px; display: flex; flex-direction: column; gap: 8px; }
#${OVERLAY_ID} .gl-ev-btn { cursor: pointer; font-size: 13px; letter-spacing: .06em; padding: 10px; border-radius: 7px;
  border: 1px solid var(--gl-border, #3a3450); background: var(--gl-panel-2, #1d1828); color: var(--gl-text, #f2f0f7); text-align: center; }
#${OVERLAY_ID} .gl-ev-btn:hover { border-color: #e7c66a; color: #f3dfa2; }
`;

let overlay: HTMLDivElement | null = null;
let onPick: ((index: number) => void) | null = null;

function ensure(): void {
  if (overlay) return;
  injectTheme();
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s);
  }
  overlay = document.createElement("div");
  overlay.id = OVERLAY_ID;
  overlay.innerHTML = `<div class="gl-ev-card"><div class="gl-ev-head"><div class="gl-ev-title">ELEVATOR</div><div class="gl-ev-x">✕</div></div><div class="gl-ev-body"></div></div>`;
  document.body.appendChild(overlay);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) closeElevator(); });
  overlay.querySelector(".gl-ev-x")!.addEventListener("click", () => closeElevator());
}

/** Open the floor picker. `pick(i)` fires with the chosen index, then the panel closes itself. */
export function openElevator(floors: { label: string }[], pick: (index: number) => void): void {
  ensure();
  onPick = pick;
  const body = overlay!.querySelector(".gl-ev-body") as HTMLDivElement;
  const esc = (s: string): string => s.replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]!));
  body.innerHTML = floors.map((f, i) => `<div class="gl-ev-btn" data-floor="${i}">${esc(f.label)}</div>`).join("");
  body.querySelectorAll<HTMLElement>("[data-floor]").forEach((el) => el.onclick = () => {
    const i = Number(el.dataset.floor);
    closeElevator();
    onPick = null;
    pick(i);
  });
  overlay!.classList.add("open");
}

export function closeElevator(): void {
  if (!overlay?.classList.contains("open")) return;
  overlay.classList.remove("open");
  onPick = null;
}

export function isElevatorOpen(): boolean { return !!overlay?.classList.contains("open"); }
